import React, { useEffect, useRef, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Generic editor for a profile schema section.
 * Renders the right input per field.type and POSTs partial updates via onSave(values).
 */
export default function SectionEditor({ section, profile, onClose, onSave }) {
  const [values, setValues] = useState(() => {
    const v = {};
    for (const f of section.fields) {
      v[f.key] = profile?.[f.key] ?? (f.type === "multiselect" ? [] : "");
    }
    return v;
  });
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const setVal = (key, val) => setValues((prev) => ({ ...prev, [key]: val }));

  const toggleMulti = (key, opt) => {
    setValues((prev) => {
      const list = Array.isArray(prev[key]) ? prev[key] : [];
      return {
        ...prev,
        [key]: list.includes(opt) ? list.filter((x) => x !== opt) : [...list, opt],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Coerce numeric / slider fields
      const out = {};
      for (const f of section.fields) {
        const raw = values[f.key];
        if (f.type === "number" || f.type === "slider") {
          if (raw === "" || raw === null || raw === undefined) {
            out[f.key] = null;
          } else {
            const n = Number(raw);
            out[f.key] = Number.isNaN(n) ? null : n;
          }
        } else if (f.type === "multiselect") {
          out[f.key] = Array.isArray(raw) ? raw : [];
        } else {
          out[f.key] = typeof raw === "string" ? raw.trim() : raw;
        }
      }
      await onSave(out);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-qh-ink/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid={`section-editor-${section.id}`}
    >
      <div
        ref={dialogRef}
        className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 p-6 sm:p-8 border-b border-qh-line shrink-0">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-medium text-qh-ink leading-tight">
              {section.title}
            </h2>
            {section.description && (
              <p className="mt-1 text-sm text-qh-slate">{section.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full border border-qh-line flex items-center justify-center text-qh-slate hover:text-qh-ink shrink-0"
            data-testid="editor-close"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6"
        >
          {section.fields.map((f) => (
            <Field key={f.key} field={f} value={values[f.key]} setVal={setVal} toggleMulti={toggleMulti} />
          ))}

          <div className="flex items-center justify-end gap-3 pt-4 sticky bottom-0 bg-white -mx-6 sm:-mx-8 px-6 sm:px-8 pb-1 -mb-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              data-testid="editor-cancel"
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-qh-rose hover:bg-qh-rosedark text-white rounded-full px-6"
              data-testid="editor-save"
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Speichern…
                </span>
              ) : (
                "Speichern"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ field, value, setVal, toggleMulti }) {
  const id = `field-${field.key}`;
  const baseInput =
    "w-full rounded-xl border border-qh-line bg-white px-4 py-3 text-qh-ink focus:outline-none focus:border-qh-rose focus:ring-4 focus:ring-qh-rose/10 transition";

  if (field.type === "text" || field.type === "url") {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-qh-ink mb-2">
          {field.label}
          {field.optional && <span className="text-qh-slate/60 font-normal"> (optional)</span>}
        </label>
        <input
          id={id}
          type={field.type === "url" ? "url" : "text"}
          value={value || ""}
          onChange={(e) => setVal(field.key, e.target.value)}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          className={baseInput}
          data-testid={`input-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-qh-ink mb-2">
          {field.label}
        </label>
        <textarea
          id={id}
          value={value || ""}
          onChange={(e) => setVal(field.key, e.target.value)}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          rows={4}
          className={`${baseInput} resize-none`}
          data-testid={`input-${field.key}`}
        />
        {field.maxLength && (
          <div className="text-right text-xs text-qh-slate/70 mt-1">
            {(value || "").length}/{field.maxLength}
          </div>
        )}
      </div>
    );
  }

  if (field.type === "number") {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-qh-ink mb-2">
          {field.label}
        </label>
        <input
          id={id}
          type="number"
          value={value === null || value === undefined ? "" : value}
          onChange={(e) => setVal(field.key, e.target.value === "" ? "" : Number(e.target.value))}
          min={field.min}
          max={field.max}
          className={baseInput}
          data-testid={`input-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "date") {
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-qh-ink mb-2">
          {field.label}
        </label>
        <input
          id={id}
          type="date"
          value={value || ""}
          onChange={(e) => setVal(field.key, e.target.value)}
          className={baseInput}
          data-testid={`input-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "radio") {
    return (
      <div>
        <span className="block text-sm font-medium text-qh-ink mb-2">{field.label}</span>
        <div className="flex flex-wrap gap-2">
          {field.options.map((opt) => {
            const active = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => setVal(field.key, active ? "" : opt)}
                className={`text-sm px-4 py-2 rounded-full border transition ${
                  active
                    ? "bg-qh-rose text-white border-qh-rose"
                    : "bg-white text-qh-slate border-qh-line hover:border-qh-rose"
                }`}
                data-testid={`radio-${field.key}-${opt}`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === "multiselect") {
    return (
      <div>
        <span className="block text-sm font-medium text-qh-ink mb-2">{field.label}</span>
        <div className="flex flex-wrap gap-2">
          {field.options.map((opt) => {
            const active = Array.isArray(value) && value.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggleMulti(field.key, opt)}
                className={`text-sm px-4 py-2 rounded-full border transition ${
                  active
                    ? "bg-qh-rose text-white border-qh-rose"
                    : "bg-white text-qh-slate border-qh-line hover:border-qh-rose"
                }`}
                data-testid={`multi-${field.key}-${opt}`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === "slider") {
    const v = typeof value === "number" ? value : 50;
    return (
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-qh-ink mb-2">
          {field.label}
        </label>
        <input
          id={id}
          type="range"
          min={0}
          max={100}
          step={1}
          value={v}
          onChange={(e) => setVal(field.key, Number(e.target.value))}
          className="w-full accent-qh-rose"
          data-testid={`slider-${field.key}`}
        />
        <div className="mt-1 flex justify-between text-xs text-qh-slate">
          <span>{field.leftLabel}</span>
          <span className="font-semibold text-qh-rose">{v}</span>
          <span>{field.rightLabel}</span>
        </div>
      </div>
    );
  }

  return null;
}
